import { X, Coins, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useStyleCredits, CreditAction, CREDIT_COSTS } from '@/hooks/useStyleCredits';

interface InsufficientCreditsModalProps {
  action: CreditAction;
  onClose: () => void;
  onBuyCredits: () => void;
}

const ACTION_LABELS: Record<CreditAction, string> = {
  outfit_generation: 'Outfit Generation',
  ootd_rating: 'OOTD Rating',
  virtual_tryon: 'Virtual Try-On',
  agent_chat: 'AI Chat',
  clothing_analysis: 'Clothing Analysis',
  packing_list: 'Packing List'
};

export function InsufficientCreditsModal({ 
  action, 
  onClose, 
  onBuyCredits 
}: InsufficientCreditsModalProps) {
  const { balance } = useStyleCredits();
  const required = CREDIT_COSTS[action];
  const shortfall = required - balance;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="w-full sm:max-w-sm p-5 relative rounded-t-2xl sm:rounded-2xl shadow-2xl bg-card border border-border safe-bottom">
        <button 
          onClick={onClose} 
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <X size={18} />
        </button>
        
        <div className="text-center mb-5">
          <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-destructive/10 flex items-center justify-center">
            <AlertTriangle size={24} className="text-destructive" />
          </div>
          <h3 className="text-lg font-bold text-foreground">Not Enough Credits</h3>
          <p className="text-sm text-muted-foreground mt-1">
            You need more for {ACTION_LABELS[action]}
          </p>
        </div>

        <div className="space-y-2 mb-5">
          <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
            <span className="text-sm text-muted-foreground">Required</span>
            <span className="font-semibold text-foreground flex items-center gap-1.5">
              <Coins size={14} className="text-primary" />
              {required}
            </span>
          </div>
          <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
            <span className="text-sm text-muted-foreground">Your Balance</span>
            <span className="font-semibold text-destructive flex items-center gap-1.5">
              <Coins size={14} />
              {balance}
            </span>
          </div>
          <div className="flex justify-between items-center p-3 rounded-xl bg-destructive/10 border border-destructive/20">
            <span className="text-sm font-medium text-foreground">Need</span>
            <span className="font-bold text-destructive flex items-center gap-1.5">
              <Coins size={14} />
              {shortfall}+
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Button
            onClick={() => {
              onClose();
              onBuyCredits();
            }}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 text-base"
          >
            <Coins size={18} className="mr-2" />
            Get Style Credits
          </Button>
          <Button
            onClick={onClose}
            variant="ghost"
            className="w-full h-11"
          >
            Maybe Later
          </Button>
        </div>
      </div>
    </div>
  );
}
